import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { CATEGORIES } from "@/lib/seo-categories";
import { getSeoPage } from "@/lib/seo-content";
import { resolveAssetPath } from "@/lib/seo-image-loader";
import SEOHead from "@/components/seo/SEOHead";
import { breadcrumbJsonLd } from "@/lib/page-seo";

const titleFromH1 = (h1: string) =>
  h1.split(" — ")[0].split(" in Kalyan")[0].trim();

const ServicesIndex = () => (
  <article>
    <SEOHead
      title="Dental Services in Kalyan | Mutha's Advanced Dental Care"
      description="Explore all dental services at Mutha's Advanced Dental Care Kalyan — general, cosmetic, restorative, orthodontics and specialty care. 25 dedicated treatment pages with pricing, FAQs and bookings."
      canonical="/services"
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Dental Services in Kalyan",
          url: "https://muthaadvanceddentalcare.in/services",
          isPartOf: {
            "@type": "WebSite",
            name: "Mutha's Advanced Dental Care",
            url: "https://muthaadvanceddentalcare.in/",
          },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: CATEGORIES.map((cat, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: cat.name,
              item: `https://muthaadvanceddentalcare.in/services/${cat.hubSlug}`,
              url: `https://muthaadvanceddentalcare.in/services/${cat.hubSlug}`,
            })),
          },
        },
        breadcrumbJsonLd([{ name: "Services", path: "/services" }]),
      ]}
    />

    {/* Hero */}
    <section className="bg-gradient-to-br from-secondary/40 via-background to-accent/30 py-14 md:py-20 border-b border-border">
      <div className="container max-w-3xl text-center">
        <span className="text-eyebrow text-primary mb-3 block">Our Services</span>
        <h1 className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-light tracking-tight leading-[1.15] mb-5">
          Complete Dental Care in Kalyan — All Under One Roof
        </h1>
        <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
          Five specialty departments. 25 dedicated treatment pages. One trusted clinic in Kalyan.
        </p>
      </div>
    </section>

    {/* Category cards */}
    <section className="py-14 md:py-20">
      <div className="container">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const hub = getSeoPage(cat.hubSlug);
            const img = hub ? resolveAssetPath(hub.heroImage) : "";
            return (
              <Link key={cat.key} to={`/services/${cat.hubSlug}`} className="group">
                <Card className="overflow-hidden h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-border">
                  {img && (
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                      <img
                        src={img}
                        alt={cat.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Icon className="h-5 w-5" />
                      </span>
                      <h2 className="font-heading text-lg font-medium">{cat.name}</h2>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{cat.blurb}</p>
                    <span className="text-sm font-medium text-primary inline-flex items-center gap-1">
                      Explore {cat.shortName} <ArrowRight className="h-4 w-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>

    {/* All services grouped */}
    <section className="py-14 md:py-20 bg-muted/30 border-t border-border">
      <div className="container">
        <div className="text-center mb-10">
          <span className="text-eyebrow text-primary">Full Service List</span>
          <h2 className="font-heading text-2xl md:text-3xl font-light mt-2">All Mutha's Advanced Dental Care Treatments</h2>
        </div>
        <div className="space-y-12">
          {CATEGORIES.map((cat) => (
            <div key={cat.key}>
              <h3 className="font-heading text-xl font-medium mb-5 text-foreground/90">{cat.name}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {cat.serviceSlugs.map((slug) => {
                  const page = getSeoPage(slug);
                  if (!page) return null;
                  return (
                    <Link key={slug} to={`/services/${slug}`} className="group">
                      <Card className="h-full hover:shadow-md hover:border-primary/40 transition-all">
                        <CardContent className="p-4">
                          <h4 className="font-heading font-semibold text-sm mb-1 group-hover:text-primary transition-colors">
                            {titleFromH1(page.h1)}
                          </h4>
                          <p className="text-xs text-muted-foreground line-clamp-2">{page.subheadline}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </article>
);

export default ServicesIndex;